import { ComponentManager } from "./ComponentManager.js";
import Mustache from 'mustache';

export class LayoutEngine {
    constructor(appClient) {
        this.appClient = appClient;
        this.prefix = '';
        this.layers = new Map();
        this.currentRoute = null;
        this.rootContainer = null;
        this.layoutConfig = null;
        this.componentManager = null;
        this.handleRouteChange = this.handleRouteChange.bind(this);
        this.permissionCheck = null;

        this.log = new this.appClient.Logger(this.constructor.name);

        this.defaultRoutes = {
            public: '/about', // Default for public users
            user: '/user/dashboard', // Default for logged-in users
            login: '/entry/login', //Default login page
            entry: '/entry' // Default entry points for login, signup, etc.
        };


        this.defaultStaticLayers = {

        };


        this.defaultErrorLayers = {
            error: {
                template: '<div id="error-container"></div>',
                components: [
                    {
                        name: 'not-found',
                        type: 'layer',
                        layer: 'not-found',
                        container: 'error-container'
                    },
                    {
                        name: 'unauthorized',
                        type: 'layer',
                        layer: 'unauthorized',
                        container: 'error-container'
                    }
                ]
            },
            'not-found': {
                route: '/error/not-found',
                template: `
                <div class="container-fluid">
                  <div class="d-flex align-items-center justify-content-center min-vh-100">
                    <div class="text-center">
                        <h1 class="display-1">404</h1>
                        <p class="lead" data-i18n="The page you're looking for doesn't exist.">The page you're looking for doesn't exist.</p>
                        <button id="layer-not-found-btn" class="btn btn-primary error-go-home-btn" data-i18n="Go Home">Go Home</button>
                    </div>
                   </div>
                </div>
            `
            },
            'unauthorized': {
                route: '/error/unauthorized',
                template: `
                <div class="container-fluid">
                 <div class ="d-flex align-items-center justify-content-center min-vh-100">
                    <div class="text-center">
                        <h1 class="display-1">401</h1>
                        <p class="lead" data-i18n="You don't have permission to view this page.">You don't have permission to view this page.</p>
                        <button id="layer-unauthorized-btn" class="btn btn-primary error-go-home-btn" data-i18n="Go Home">Go Home</button>
                    </div>
                 </div>
                </div>
            `
            }
        };


        this.clientRouteMatchers = []; // Cache for compiled route patterns

    }

    handleErrorButtonClick(event) {
        event.preventDefault();
        const redirectRoute = this.appClient.isLoggedIn() ? this.defaultRoutes.user : this.defaultRoutes.public;
        this.navigate(redirectRoute);
    }


    async initialize({ layoutConfig, rootContainer, appClient, prefix = '' }) {
        if (!layoutConfig || !rootContainer) {
            throw new Error("LayoutEngine initialization requires both layoutConfig and rootContainer");
        }


        this.appClient = appClient;
        this.prefix = prefix;
        this.componentManager = new ComponentManager(this);

        // If layoutConfig is a string, treat it as a path and load the config
        if (typeof layoutConfig === 'string') {
            try {
                const response = await fetch(layoutConfig);
                if (!response.ok) {
                    throw new Error(`Failed to load layout config from ${layoutConfig}`);
                }
                this.layoutConfig = await response.json();
            } catch (error) {
                console.error('Error loading layout config:', error);
                throw error;
            }
        } else {
            this.layoutConfig = layoutConfig;
        }

        // Set the permission check if provided
        if (this.layoutConfig.permissionCheck) {
            this.permissionCheck = this.layoutConfig.permissionCheck;
        }

        if (this.layoutConfig.defaultRoutes) {
            this.defaultRoutes = { ...this.defaultRoutes, ...this.layoutConfig.defaultRoutes };
        }


        const baseLayer = this.layoutConfig.layers.base;
        if (!baseLayer.components.some(comp => comp.layer === 'error')) {
            baseLayer.components.push({
                name: 'error',
                type: 'layer',
                layer: 'error',
                container: 'root-container'
            });
        }

        // Inject missing error layers
        if (!this.layoutConfig.layers.error) {
            this.layoutConfig.layers.error = this.defaultErrorLayers.error;
        }
        if (!this.layoutConfig.layers['not-found']) {
            this.layoutConfig.layers['not-found'] = this.defaultErrorLayers['not-found'];
        }
        if (!this.layoutConfig.layers.unauthorized) {
            this.layoutConfig.layers.unauthorized = this.defaultErrorLayers.unauthorized;
        }


        const mergedLayers = {
            ...this.defaultErrorLayers,
            ...this.defaultStaticLayers, // Add the new static layers
            ...this.layoutConfig.layers
        };

        this.layoutConfig.layers = mergedLayers;

        this.rootContainer = rootContainer;
        this.rootContainer.innerHTML = '';

        this.recalculateReservedHeight();

        window.addEventListener('popstate', this.handleRouteChange);
        document.addEventListener('click', (e) => this.interceptClicks(e));

        await this.buildLayerHierarchy();
        this._buildClientRouteCache();

        // Handle error layer buttons
        const errorBtn = document.getElementById('layer-not-found-btn');
        const unauthorizedBtn = document.getElementById('layer-unauthorized-btn');
        if (errorBtn) {
            errorBtn.addEventListener('click', (e) => this.handleErrorButtonClick(e));
        }
        if (unauthorizedBtn) {
            unauthorizedBtn.addEventListener('click', (e) => this.handleErrorButtonClick(e));
        }

        const currentPath = this.stripPrefix(window.location.pathname);
        if (currentPath === '/' && this.layoutConfig.defaultRoute) {
            await this.navigate(this.layoutConfig.defaultRoute);
        } else {
            await this.handleRouteChange();
        }

        this.appClient.events.server.on('reloadComponent', this.reloadComponent.bind(this));


    }

    async buildLayerHierarchy() {
        // Create all layer elements first
        for (const [layerName, layerConfig] of Object.entries(this.layoutConfig.layers)) {
            await this.createLayer(layerName, layerConfig);
            if (layerName === 'base') {
                this.rootContainer.appendChild(this.layers.get(layerName).container);
            }
        }

        // Then establish the hierarchy by mounting layers and components
        for (const [layerName, layerConfig] of Object.entries(this.layoutConfig.layers)) {
            await this.mountLayer(layerName, layerConfig);
        }
    }


    _buildClientRouteCache() {
        if (this.clientRouteMatchers.length > 0) return; // Already built

        const patterns = new Set();
        for (const layer of this.layers.values()) {
            if (layer.config.route) {
                patterns.add(layer.config.route);
            }
        }

        for (const pattern of patterns) {
            if (pattern.endsWith('/*')) {
                // Handles patterns like /user/*
                this.clientRouteMatchers.push({
                    type: 'prefix',
                    value: pattern.slice(0, -1) // Stores '/user/'
                });
            } else if (pattern.startsWith('/(') && pattern.endsWith(')')) {
                // Handles regex-like groups like /(user|admin)
                const group = pattern.substring(2, pattern.length - 1); // Extracts 'user|admin'
                this.clientRouteMatchers.push({
                    type: 'regex',
                    value: new RegExp(`^/(${group})`)
                });
            } else {
                // Handles exact paths like /entry/login and simple prefixes like /user
                this.clientRouteMatchers.push({
                    type: 'prefix',
                    value: pattern
                });
            }
        }
    }


    _executeScriptsInElement(element) {
        const scripts = element.querySelectorAll("script");

        scripts.forEach(originalScript => {
            if (originalScript.type && originalScript.type !== 'module' && originalScript.type !== 'text/javascript') {
                // Skip non-executable script types like 'importmap'
                return;
            }

            // Create a new script element. This is the crucial step.
            // Simply appending the old script won't work. The browser needs a *new* element.
            const newScript = document.createElement("script");

            // Copy all attributes from the original script to the new one (e.g., type, src, async)
            Array.from(originalScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });

            // Copy the inline script content
            if (originalScript.innerHTML) {
                newScript.innerHTML = originalScript.innerHTML;
            }

            // Replace the original script tag with the new, executable one.
            // The browser will now discover and execute this new script tag.
            originalScript.parentNode.replaceChild(newScript, originalScript);
        });
    }


    async createLayer(layerName, layerConfig) {
        let template = layerConfig.template;

        // If templateFile is specified, fetch the template
        if (layerConfig.templateFile) {
            try {
                const response = await fetch(layerConfig.templateFile);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                template = await response.text();
            } catch (error) {
                console.error(`Error fetching template file for ${layerName}:`, error);
                throw error;
            }
        }

        const renderContext = {
            appName: this.appClient.sessionTokenHandler?.clientConfig?.appName || this.appClient.config.projectName || "AppMageOS",
            currentYear: new Date().getFullYear()
            // Add any other globally available client-side variables here if needed
        };

        const renderedTemplate = Mustache.render(template || '', renderContext);


        const templateElement = document.createElement('template');
        templateElement.innerHTML = (renderedTemplate || '').trim();
        const layerElement = templateElement.content.firstElementChild;

        if (!layerElement) {
            console.error(`Template for ${layerName} is empty or invalid.`);
            return; // Or throw an error if an empty template is not allowed
        }

        layerElement.dataset.layer = layerName;
        if (layerConfig.route) {
            layerElement.style.display = 'none'
        }

        this._executeScriptsInElement(layerElement);
        this.layers.set(layerName, {
            name: layerName,
            config: {
                ...layerConfig,
                template: template
            },
            container: layerElement,
            components: new Map()
        });
    }


    async mountLayer(layerName, layerConfig) {
        const layer = this.layers.get(layerName);

        if (layerConfig.components) {
            for (const componentConfig of layerConfig.components) {
                let targetContainer = null;
                if (componentConfig.container === 'root-container') {
                    targetContainer = this.rootContainer;
                }
                else if (layer.container.id === componentConfig.container) {
                    targetContainer = layer.container;
                }
                else {
                    targetContainer = layer.container.querySelector(`#${componentConfig.container}`);
                }

                if (!targetContainer) {
                    console.error(`Container #${componentConfig.container} not found in layer ${layerName}`);
                    continue;
                }

                if (componentConfig.type === 'layer') {
                    const nestedLayer = this.layers.get(componentConfig.layer);
                    if (nestedLayer) {
                        targetContainer.appendChild(nestedLayer.container);
                    }
                } else {
                    const component = await this.createComponent(componentConfig);
                    if (component) {
                        const element = await component.render();
                        targetContainer.appendChild(element);
                        layer.components.set(componentConfig.name, {
                            config: componentConfig,
                            element: element,
                            instance: component,
                        });
                    } else {
                        console.error(`Skipping mounting component ${componentConfig.name || componentConfig.type} because it failed to load.`);
                    }
                }
            }
        }
    }

    async createComponent(componentConfig) {
        try {
            const component = await this.componentManager.getOrCreateComponent(componentConfig);
            return component;
        } catch (error) {
            console.error(`Error creating component ${componentConfig.name}:`, error);
            return null;
        }
    }

    matchesRoute(route, currentRoute, isExact) {
        if (!route) return false;

        // Support route parameters like /user/machines/:machineId
        if (typeof route === 'string' && route.includes(':')) {
            const pattern = route.split('/').map(seg => seg.startsWith(':') ? '[^/]+' : seg).join('/');
            const regex = new RegExp(isExact ? `^${pattern}$` : `^${pattern}(/|$)`);
            return regex.test(currentRoute);
        }

        if (isExact) return currentRoute === route;
        if (currentRoute === route) return true;

        if (currentRoute.startsWith(route)) {
            // Ensure it's a full segment match (e.g. /user/settings matches /user/settings/profile but not /user/settings-config)
            return route.endsWith('/') || currentRoute[route.length] === '/';
        }
        return false;
    }

    getMatchingLayers() {
        const matchingLayers = new Set();

        // First find directly matching layers and their ancestors
        for (const layer of this.layers.values()) {
            if (this.matchesRoute(layer.config.route, this.currentRoute, layer.config.isExact)) {
                // Add the matching layer, its ancestors, and its descendants
                this.addLayerChain(layer.name, matchingLayers);

                if (layer.config.components) {
                    for (const comp of layer.config.components) {
                        if (comp.type === 'layer') {
                            this.addLayerChain(comp.layer, matchingLayers);
                        }
                    }
                }
            }
        }

        return Array.from(matchingLayers);
    }

    addLayerChain(layerName, matchingLayers) {
        const layer = this.layers.get(layerName);
        if (!layer || matchingLayers.has(layer)) return;

        matchingLayers.add(layer);

        // Add ancestors (parent layers)
        for (const [parentName, parentLayer] of this.layers.entries()) {
            if (parentLayer.config.components?.some(comp =>
                comp.type === 'layer' && comp.layer === layerName
            )) {
                this.addLayerChain(parentName, matchingLayers);
            }
        }
    }

    _removeDescendantsFromSet(layer, visibleSet) {
        if (layer.config.components) {
            for (const comp of layer.config.components) {
                if (comp.type === 'layer') {
                    const childLayer = this.layers.get(comp.layer);
                    if (childLayer && visibleSet.has(childLayer)) {
                        visibleSet.delete(childLayer);
                        this._removeDescendantsFromSet(childLayer, visibleSet);
                    }
                }
            }
        }
    }

    async updateLayout(matchingLayers) {
        // Track which layers were visible BEFORE this update
        const previouslyVisibleLayers = new Set(
            Array.from(this.layers.values())
                .filter(layer => layer.container.style.display !== 'none')
        );

        let currentlyVisibleLayers = new Set(matchingLayers);

        // PRE-STEP: Filter currentlyVisibleLayers so that only ONE layer per container is visible
        const preContainerGroups = new Map();
        for (const layer of currentlyVisibleLayers) {
            const parentElement = layer.container.parentElement;
            if (parentElement) {
                if (!preContainerGroups.has(parentElement)) {
                    preContainerGroups.set(parentElement, []);
                }
                preContainerGroups.get(parentElement).push(layer);
            }
        }

        for (const [container, layers] of preContainerGroups) {
            if (layers.length > 1) {
                const exactMatch = layers.find(layer =>
                    this.matchesRoute(layer.config.route, this.currentRoute, true)
                );

                let layerToKeep;
                if (exactMatch) {
                    layerToKeep = exactMatch;
                } else {
                    const parentLayer = Array.from(this.layers.values()).find(
                        l => l.container === container || l.container.contains(container)
                    );
                    
                    let defaultLayer;
                    if (parentLayer && parentLayer.config.components) {
                        const defaultComp = parentLayer.config.components.find(comp => comp.type === 'layer' && comp.default);
                        if (defaultComp) {
                            defaultLayer = layers.find(l => l.name === defaultComp.layer);
                        }
                    }
                    
                    layerToKeep = defaultLayer || layers[0];
                }

                for (const layer of layers) {
                    if (layer !== layerToKeep) {
                        currentlyVisibleLayers.delete(layer);
                        this._removeDescendantsFromSet(layer, currentlyVisibleLayers);
                    }
                }
            }
        }

        matchingLayers = Array.from(currentlyVisibleLayers);

        // STEP 1: Hide layers that should no longer be visible and call onHide
        // Only process layers that were visible before AND are not in the new visible set
        for (const layer of previouslyVisibleLayers) {
            if (!currentlyVisibleLayers.has(layer)) {
                // Call onHide for components that were actually visible
                if (layer.components) {
                    for (const componentData of layer.components.values()) {
                        if (componentData.element.style.display !== 'none' &&
                            componentData.instance &&
                            typeof componentData.instance.onHide === 'function') {
                            await componentData.instance.onHide();
                        }
                    }
                }

                // Hide the layer
                if (layer.config.route) {
                    layer.container.style.setProperty('display', 'none', 'important');
                }

                this.appClient.events.fireLocal('layer:hidden', {
                    layer: layer.name,
                    config: layer.config
                });
            }
        }

        // STEP 2: Hide ALL routable layers that shouldn't be visible
        // This is critical for initial load where layers may have never been shown
        // For layers that were just hidden in STEP 1, this is redundant but harmless
        for (const layer of this.layers.values()) {
            if (layer.config.route && !currentlyVisibleLayers.has(layer)) {
                // This ensures even layers that were never visible get hidden
                layer.container.style.setProperty('display', 'none', 'important');
            }
        }

        // STEP 3: Explicitly ensure 'base' layer is always visible
        const baseLayer = this.layers.get('base');
        if (baseLayer) {
            baseLayer.container.style.removeProperty('display');
        }

        // STEP 4: Group matching layers by parent container
        const containerGroups = new Map();
        for (const layer of matchingLayers) {
            const parentElement = layer.container.parentElement;
            if (parentElement) {
                if (!containerGroups.has(parentElement)) {
                    containerGroups.set(parentElement, []);
                }
                containerGroups.get(parentElement).push(layer);
            }
        }

        // STEP 5: Show the correct layer in each container group
        for (const [container, layers] of containerGroups) {
            for (const layer of layers) {
                const layerWasHidden = !previouslyVisibleLayers.has(layer);
                layer.container.style.removeProperty('display');
                await this.updateComponentsVisibility(layer, layerWasHidden);
            }
        }

        // STEP 6: Handle remaining layers without parent element
        for (const layer of matchingLayers) {
            if (!layer.container.parentElement) {
                const layerWasHidden = !previouslyVisibleLayers.has(layer);
                layer.container.style.removeProperty('display');
                await this.updateComponentsVisibility(layer, layerWasHidden);
            }
        }

        // STEP 7: Fire visible events for newly shown layers
        for (const layer of matchingLayers) {
            if (!previouslyVisibleLayers.has(layer)) {
                this.appClient.events.fireLocal('layer:visible', {
                    layer: layer.name,
                    config: layer.config
                });
            }
        }
    }


    async updateComponentsVisibility(layer, layerWasHidden = false) {
        if (!layer.components.size) return;

        // Track which components were visible BEFORE this update
        const previouslyVisible = new Map();
        for (const [name, componentData] of layer.components.entries()) {
            // If the layer itself was hidden, all its components were effectively hidden
            const isEffectivelyVisible = !layerWasHidden && componentData.element.style.display !== 'none';
            previouslyVisible.set(name, isEffectivelyVisible);
        }

        // Group components by their parent container
        const containerMap = new Map();
        for (const [_, componentData] of layer.components.entries()) {
            const container = componentData.element.parentElement;
            if (!containerMap.has(container)) {
                containerMap.set(container, []);
            }
            containerMap.get(container).push(componentData);
        }

        // Update visibility for each container's components
        for (const components of containerMap.values()) {
            const defaultComponent = components.find(comp => comp.config.default);
            const exactRouteMatch = components.find(comp =>
                comp.config.route && this.matchesRoute(comp.config.route, this.currentRoute, true)
            );

            const componentToShow = exactRouteMatch || defaultComponent || components[0];

            for (const comp of components) {
                const wasVisible = previouslyVisible.get(comp.config.name);
                const willBeVisible = comp === componentToShow;

                // Only process if visibility actually changed
                if (wasVisible !== willBeVisible) {
                    if (willBeVisible) {
                        comp.element.style.removeProperty('display');
                    } else {
                        comp.element.style.setProperty('display', 'none', 'important');
                    }

                    // Call lifecycle methods ONLY when visibility changes
                    if (comp.instance) {
                        if (willBeVisible) {
                            if (typeof comp.instance.onShow === 'function') {
                                await comp.instance.onShow();
                            }
                        } else {
                            if (typeof comp.instance.onHide === 'function') {
                                await comp.instance.onHide();
                            }
                        }
                    }

                    // Fire events
                    this.appClient.events.fireLocal(
                        willBeVisible ? 'component:visible' : 'component:hidden',
                        {
                            component: comp.config.name,
                            type: comp.config.type,
                            layer: layer.name
                        }
                    );
                }

                // Refresh visible component (even if it was already visible)
                if (willBeVisible && comp.instance?.refresh) {
                    await comp.instance.refresh();
                }
            }
        }
    }

    async handleRouteChange() {
        this.currentRoute = this.stripPrefix(window.location.pathname);

        // Skip permission check for error routes to avoid infinite loops
        if (this.currentRoute.startsWith('/error/')) {
            const matchingLayers = this.getMatchingLayers();
            await this.updateLayout(matchingLayers);
            return;
        }

        const permissionCheckFunction = this.permissionCheck?.bind(this) || this.defaultPermissionCheck.bind(this);
        if (permissionCheckFunction) {
            try {
                const result = await permissionCheckFunction(this.appClient, this.currentRoute);
                if (!result.allowed) {
                    await this.navigate(result.redirectUri);
                    return;
                }
            } catch (error) {
                this.log.error('Permission check failed:', error);
                await this.navigate('/error/unauthorized');
                return;
            }
        }

        // Only check for route existence after permission check passes
        const matchingLayers = this.getMatchingLayers();
        if (matchingLayers.length === 0) {
            // Determine if this is a genuine not-found vs. a flat layout with no routed layers.
            // Flat layouts (e.g. login, lock) have all content in the 'base' layer with no route
            // property, so getMatchingLayers always returns []. In that case, we should NOT
            // redirect — updateLayout's STEP 3 will keep the base layer visible.
            const hasUserRoutedLayers = Array.from(this.layers.values()).some(
                l => l.config.route && !l.config.route.startsWith('/error/')
            );
            if (hasUserRoutedLayers) {
                // Layout has routes but none matched → genuine not-found
                await this.navigate('/error/not-found');
                return;
            }
            // Flat layout — fall through to updateLayout which shows base via STEP 3
        }

        this.appClient.events.fireLocal('route-changed', {
            route: this.currentRoute
        });

        // If all checks pass, update the layout
        await this.updateLayout(matchingLayers);

        // For flat layouts (no routed layers), explicitly initialize base layer components
        // since they are never included in matchingLayers and don't go through STEP 5.
        if (matchingLayers.length === 0) {
            const baseLayer = this.layers.get('base');
            if (baseLayer) {
                await this.updateComponentsVisibility(baseLayer, true);
            }
        }
    }

    async defaultPermissionCheck(appClient, route) {
        if (!this.appClient.sessionTokenHandler) {
            return { allowed: true };
        }

        try {
            const hasPermission = await this.appClient.sessionTokenHandler.hasPermission("routes", route);
            const isLoggedIn = await this.appClient.sessionTokenHandler.isLoggedIn();

            if (!hasPermission) {
                // If no permission, and the user is "public", redirect to login
                if (!isLoggedIn) {
                    return { allowed: false, redirectUri: this.defaultRoutes.login };
                } else {
                    return { allowed: false, redirectUri: "/error/unauthorized" };
                }
            }

            if (isLoggedIn && route.startsWith(this.defaultRoutes.entry)) {
                // pull the role(s) out of your sessionTokenHandler
                const roles = await appClient.sessionTokenHandler.getCachedRoles();
                // pick your primary role key (e.g. "admin", "student", …)
                const primary = Array.isArray(roles) ? roles[0] : roles;
                // fall back to the generic user route if you really must
                const redirectUri = this.defaultRoutes[primary] || this.defaultRoutes.user;
                return { allowed: false, redirectUri };
            }

            return { allowed: true }; // Permission granted
        } catch (error) {
            this.log.error("Error in defaultPermissionCheck:", error);
            // In case of any error, deny access and redirect to a safe error page
            return { allowed: false, redirectUri: "/error/unauthorized" };
        }
    }

    stripPrefix(route) {
        // Strip the prefix; return '/' instead of '' when the route exactly equals the prefix,
        // so the '/' === defaultRoute check in initialize() works for prefix-mounted sub-apps.
        const stripped = route.startsWith(this.prefix) ? route.slice(this.prefix.length) : route;
        return stripped || '/';
    }

    isClientRoute(pathname) {
        if (!pathname) return false;
        if (this.clientRouteMatchers.length === 0) {
            this._buildClientRouteCache();
        }

        for (const matcher of this.clientRouteMatchers) {
            switch (matcher.type) {
                case 'prefix':
                    if (pathname.startsWith(matcher.value)) {
                        if (pathname.length === matcher.value.length || pathname[matcher.value.length] === '/') {
                            return true;
                        }
                    }
                    break;
                case 'regex':
                    if (matcher.value.test(pathname)) {
                        return true;
                    }
                    break;
            }
        }

        return false;
    }


    interceptClicks(event) {
        if (event.defaultPrevented) return;
        
        const link = event.target.closest('a[href]');

        // Ignore modified clicks, external links, or anchor links
        if (!link || link.target === '_blank' || link.getAttribute('href').startsWith('#') || event.ctrlKey || event.metaKey) {
            return;
        }

        if (link.origin === window.location.origin) {
            // Before preventing default, check if the route is managed by the client-side SPA.
            if (this.isClientRoute(link.pathname)) {
                event.preventDefault();
                const route = this.stripPrefix(link.pathname) + link.search;
                this.navigate(route);
            }
        }
    }


    async navigate(route) {
        history.pushState({}, '', this.prefix + route);
        await this.handleRouteChange();
    }


    // Replace these methods in your LayoutEngine class

    getMenuStructure(startingRoute = null, includeHidden = false) {
        const menuItems = [];

        // If startingRoute is "/" or null, collect all menu items from all layers
        if (!startingRoute || startingRoute === "/") {
            return this._getAllMenuItems(includeHidden);
        }

        // Find the starting layer
        let startingLayer = null;
        startingLayer = Array.from(this.layers.values()).find(
            (layer) => layer.config.route === startingRoute
        );

        if (!startingLayer) {
            console.warn(`No layer found for starting route: ${startingRoute}`);
            // Fall back to collecting all menu items
            return this._getAllMenuItems(includeHidden);
        }

        // Process the starting layer and its children
        this._processLayerForMenu(
            startingLayer,
            menuItems,
            includeHidden,
            ""
        );

        return this._sortAndGroupMenuItems(menuItems);
    }

    // New helper method to collect all menu items when no specific starting route
    _getAllMenuItems(includeHidden = false) {
        const menuItems = [];

        for (const layer of this.layers.values()) {
            const { config } = layer;

            // Skip special routes automatically
            if (this._isSpecialRoute(config.route)) {
                continue;
            }

            // Check if this layer should be included in menu
            const shouldInclude = includeHidden || config.menuItem || (config.route && !config.hideInMenu);

            if (shouldInclude && config.route) {
                const menuItem = {
                    route: config.route,
                    title: config.menuTitle || this._generateTitleFromRoute(config.route) || config.name,
                    icon: config.menuIcon,
                    order: config.menuOrder || 0,
                    group: config.menuGroup,
                    children: []
                };

                menuItems.push(menuItem);
            }
        }

        return this._sortAndGroupMenuItems(menuItems);
    }

    _processLayerForMenu(layer, menuItems, includeHidden, parentPath = '') {
        const { config } = layer;

        // Skip special routes automatically
        if (this._isSpecialRoute(config.route)) {
            return;
        }

        // Check if this layer should be included in menu
        const shouldInclude = includeHidden || config.menuItem || config.route;

        // Special handling for the 'base' layer - process its children without adding itself
        if (layer.name === 'base' && !config.route) {
            if (config.components) {
                for (const component of config.components) {
                    if (component.type === 'layer') {
                        const childLayer = this.layers.get(component.layer);
                        if (childLayer) {
                            this._processLayerForMenu(childLayer, menuItems, includeHidden, parentPath);
                        }
                    }
                }
            }
            return;
        }

        if (shouldInclude) {
            const menuItem = {
                route: config.route,
                title: config.menuTitle || this._generateTitleFromRoute(config.route) || config.name,
                icon: config.menuIcon,
                order: config.menuOrder || 0,
                group: config.menuGroup,
                fullPath: this.buildFullPath(parentPath, config.route),
                children: []
            };

            // Only add if it has a route or should be included when hidden
            if (config.route || (includeHidden && menuItem.title)) {
                menuItems.push(menuItem);
            }

            // Process child components
            if (config.components) {
                for (const component of config.components) {
                    if (component.type === 'layer') {
                        const childLayer = this.layers.get(component.layer);
                        if (childLayer) {
                            this._processLayerForMenu(childLayer, menuItem.children, includeHidden, menuItem.fullPath);
                        }
                    }
                }
            }
        }
    }

    getFlatMenuStructure(startingRoute = null, includeHidden = false) {
        const menuItems = [];

        for (const layer of this.layers.values()) {
            const { config } = layer;

            // Skip special routes automatically
            if (this._isSpecialRoute(config.route)) {
                continue;
            }

            // Check if the layer should be included in the menu based on route and visibility
            const isVisible = includeHidden || config.menuItem || (config.route && !config.hideInMenu);

            // Filter by starting route if specified
            let matchesRoute = true;
            if (startingRoute && startingRoute !== "/") {
                const prefix = startingRoute.endsWith('/') ? startingRoute : startingRoute + '/';
                matchesRoute = config.route && config.route !== startingRoute && config.route.startsWith(prefix);
            }

            if (isVisible && matchesRoute && config.route) {
                const menuItem = {
                    route: config.route,
                    title: config.menuTitle || this._generateTitleFromRoute(config.route) || config.name,
                    icon: config.menuIcon,
                    order: config.menuOrder || 0,
                    group: config.menuGroup,
                };

                menuItems.push(menuItem);
            }
        }

        // Sort menu items by order
        menuItems.sort((a, b) => a.order - b.order);

        return menuItems;
    }

    _isSpecialRoute(route) {
        if (route && route.indexOf('|') > 0) return true;

        const specialRoutes = [
            '/entry',
            '/landing',
            '/error',
            '/privacy',
            '/terms',
            '/entry/login',
            '/entry/forgot-password',
            '/entry/signup',
            '/error/not-found',
            '/error/unauthorized',
        ];

        return specialRoutes.includes(route);
    }

    // Helper method to build fullPath correctly
    buildFullPath(parentPath, route) {
        if (!route) return parentPath;
        return parentPath ? `${parentPath}/${route}`.replace(/\/{2,}/g, '/') : route;
    }


    /**
     * Sorts and groups menu items based on order and group properties
     * @private
     */
    _sortAndGroupMenuItems(menuItems) {
        // First sort by order
        menuItems.sort((a, b) => {
            if (a.order !== b.order) {
                return a.order - b.order;
            }
            // If orders are equal, sort alphabetically by title
            return a.title.localeCompare(b.title);
        });

        // If there are groups, organize items by group
        const hasGroups = menuItems.some(item => item.group);
        if (hasGroups) {
            const groupedItems = {};
            const ungroupedItems = [];

            menuItems.forEach(item => {
                if (item.group) {
                    if (!groupedItems[item.group]) {
                        groupedItems[item.group] = [];
                    }
                    groupedItems[item.group].push(item);
                } else {
                    ungroupedItems.push(item);
                }
            });

            // Convert groups to array structure
            const result = [];
            Object.entries(groupedItems).forEach(([groupName, items]) => {
                result.push({
                    title: groupName,
                    isGroup: true,
                    children: items
                });
            });

            // Add ungrouped items at the end
            return [...result, ...ungroupedItems];
        }

        return menuItems;
    }

    /**
     * Generates a display title from a route path
     * @private
     */
    _generateTitleFromRoute(route) {
        // Get the last segment of the route
        const segment = route.split('/').pop();
        // Convert kebab-case or snake_case to Title Case
        return segment
            .split(/[-_]/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    /**
     * Gets a flat list of all routable links from a specific point in the layout
     * @param {string} [startingRoute] - Optional route to start from
     * @param {boolean} [includeHidden=false] - Whether to include items without menuItem property
     * @returns {Array<string>} Array of routes
     */
    getRoutableLinks(startingRoute = null, includeHidden = false) {
        const menuStructure = this.getMenuStructure(startingRoute, includeHidden);
        const routes = [];

        const extractRoutes = (items) => {
            items.forEach(item => {
                if (item.route) {
                    routes.push(item.route);
                }
                if (item.children?.length > 0) {
                    extractRoutes(item.children);
                }
            });
        };

        extractRoutes(menuStructure);
        return routes;
    }

    /**
 * Parse query string into an object
 * @returns {Object} Parsed query parameters
 */
    getQueryParams() {
        const params = new URLSearchParams(window.location.search);
        const result = {};

        for (const [key, value] of params.entries()) {
            // Handle array parameters (key[])
            if (key.endsWith('[]')) {
                const cleanKey = key.slice(0, -2);
                if (!result[cleanKey]) {
                    result[cleanKey] = [];
                }
                result[cleanKey].push(value);
                continue;
            }

            // Handle nested parameters (key[nested])
            const nestedMatch = key.match(/^([^\[]+)\[([^\]]+)\]$/);
            if (nestedMatch) {
                const [, parentKey, childKey] = nestedMatch;
                if (!result[parentKey]) {
                    result[parentKey] = {};
                }
                result[parentKey][childKey] = value;
                continue;
            }

            // Regular key-value
            result[key] = value;
        }

        return result;
    }

    registerViewportHeight(component, height) {}
    unregisterViewportHeight(component) {}
    recalculateReservedHeight() {}
    getViewportReservedHeight() { return 0; }
    async reloadComponent({ name }) {
        this.log.info(`Reloading component: ${name}`);
        try {
            for (const [layerName, layer] of this.layers.entries()) {
                for (const [componentName, componentData] of layer.components.entries()) {
                    if (componentData.config.type === name) {

                        // Cache the parent container before destroy
                        const parent = componentData.element.parentNode;
                        const state = componentData.instance.getState();
                        if (componentData.instance && typeof componentData.instance.destroy === 'function') {
                            componentData.instance.removeStyles();
                            await componentData.instance.destroy();
                        }

                        this.log.info(`Re-rendering component instance: ${componentName} in layer: ${layerName}`);
                        const newComponent = await this.componentManager.getOrCreateComponent(componentData.config, false);
                        const newElement = await newComponent.render();
                        newComponent.restoreState(state);

                        // Only replace if parent still contains the element
                        if (parent && parent.contains(componentData.element)) {
                            parent.replaceChild(newElement, componentData.element);
                        } else if (parent) {
                            // If parent exists but the element is no longer there, append the new element
                            parent.appendChild(newElement);
                            this.log.warn(`Component ${componentName} was already removed; appended instead.`);
                        } else {
                            // If there's no parent available, fall back to another known container
                            this.log.warn(`Component ${componentName} parent not found. Appending to layer container.`);
                            layer.container.appendChild(newElement);
                        }

                        // Update the component data
                        componentData.instance = newComponent;
                        componentData.element = newElement;
                    }
                }
            }
        } catch (error) {
            this.log.error(`Error reloading component ${name}:`, error);
            // Optionally, you can add further error handling here
        }
    }

    navigateToHome() {
        const homeRoute = this._getHomeRouteForCurrentUser();
        this.navigate(homeRoute);
    }

    _getHomeRouteForCurrentUser() {
        // 1. Get Context
        const isLoggedIn = this.appClient.isLoggedIn();
        if (!isLoggedIn) {
            return this.defaultRoutes.public || '/';
        }

        // Ensure currentRoute is a string, even if it hasn't been set yet during initial render.
        const currentRoute = this.currentRoute || '';

        const userRoles = this.appClient.sessionTokenHandler.getCachedRoles();
        const primaryRole = userRoles[0] || 'user';
        const primaryRoleHome = this.defaultRoutes[primaryRole] || this.defaultRoutes.user;

        // 2. Identify Current Section and Target Route
        let targetRoute = null;
        let bestMatchLength = 0;

        for (const role in this.defaultRoutes) {
            const routePath = this.defaultRoutes[role];
            // A null/undefined routePath can cause errors, so skip it.
            if (!routePath) continue;

            const baseSegment = '/' + (routePath.split('/')[1] || '');

            if (baseSegment !== '/' && currentRoute.startsWith(baseSegment)) {
                if (baseSegment.length > bestMatchLength) {
                    targetRoute = routePath;
                    bestMatchLength = baseSegment.length;
                }
            }
        }

        // 3. If no section match, default to the user's primary home.
        if (!targetRoute) {
            targetRoute = primaryRoleHome;
        }

        // 4. Check Permission for the determined target route.
        const hasPermission = this.appClient.sessionTokenHandler.hasPermission('routes', targetRoute);

        // 5. Return Final URL
        if (hasPermission) {
            return targetRoute;
        } else {
            return primaryRoleHome;
        }
    }

}
